import { useMemo } from 'react'
import { observable, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { ArrowRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
    Address,
    OrganizationClassificationArea,
    OrganizationClassificationAreaSchema,
    OrganizationClassificationSport,
    OrganizationClassificationSportSchema,
    OrganizationClassificationType,
    OrganizationClassificationTypeSchema,
    type OrganizationCountryCode,
} from '@campai/lib-common/entities'
import {
    Button as NewButton,
    Combobox,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
    ComboboxValue,
    Field,
    FieldDescription,
    FieldError,
    FieldLabel,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
    useComboboxAnchor,
} from '@campai/lib-web/components/new/ui'
import { AddressForm } from '@campai/lib-web/components'

import { Button } from './Button'
import { CreateOrganizationInput, Step } from './types'
import { newOrganizationAvailableCountries } from './utils'

const Element = observer(
    ({ input: { organization, ui }, onNext }: { input: CreateOrganizationInput; onNext: undefined | (() => void) }) => {
        const { t } = useTranslation()
        const i18nKey = 'createOrganization.organization'
        const errors = ui.validationErrors
        const sportsAnchor = useComboboxAnchor()

        const sportValues = useMemo(
            () => Object.keys(OrganizationClassificationSportSchema.enum) as Array<OrganizationClassificationSport>,
            []
        )
        const sportsOptions = useMemo(
            () =>
                sportValues.map((sport) => ({ value: sport, label: t(`organization.classification.sports.${sport}`) })),
            [sportValues, t]
        )
        const selectedSportsOptions = useMemo(
            () =>
                organization.classification.sports
                    .map((sport) => sportsOptions.find((option) => option.value === sport) ?? null)
                    .filter((option): option is (typeof sportsOptions)[number] => !!option),
            [organization.classification.sports, sportsOptions]
        )

        return (
            <div>
                <div className="mb-2 text-xl font-medium">
                    {t(`${i18nKey}.title`, {
                        organizationName: organization.name.trim() || t('createOrganization.welcome.namePlaceholder'),
                    })}
                </div>
                <div className="text-base leading-7 text-neutral-600">{t(`${i18nKey}.description`)}</div>

                <div className="mt-6 space-y-5">
                    <div className="grid gap-5 md:grid-cols-[5fr_6fr]">
                        <Field className="min-w-0">
                            <FieldLabel required>{t(`${i18nKey}.classificationTypeLabel`)}</FieldLabel>
                            <Select
                                value={organization.classification.type || ''}
                                onValueChange={(value) =>
                                    runInAction(() => {
                                        const result = OrganizationClassificationTypeSchema.safeParse(value)

                                        if (!result.success) {
                                            return
                                        }

                                        organization.classification.type = result.data
                                    })
                                }
                            >
                                <SelectTrigger
                                    className="h-11 w-full border-neutral-200 bg-white"
                                    aria-invalid={!!errors?.getError('organization.classification.type')}
                                >
                                    <SelectValue>
                                        {organization.classification.type
                                            ? t(`organization.classification.type.${organization.classification.type}`)
                                            : t(`${i18nKey}.classificationTypePlaceholder`)}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {(
                                            Object.keys(
                                                OrganizationClassificationTypeSchema.enum
                                            ) as Array<OrganizationClassificationType>
                                        ).map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {t(`organization.classification.type.${option}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <FieldError>{errors?.getError('organization.classification.type')}</FieldError>
                        </Field>

                        <Field className="min-w-0">
                            <FieldLabel required>{t(`${i18nKey}.classificationAreaLabel`)}</FieldLabel>
                            <Select
                                value={organization.classification.area || ''}
                                onValueChange={(value) =>
                                    runInAction(() => {
                                        const result = OrganizationClassificationAreaSchema.safeParse(value)

                                        if (!result.success) {
                                            return
                                        }

                                        organization.classification.area = result.data

                                        if (result.data !== 'sports') {
                                            organization.classification.sports = []
                                        }
                                    })
                                }
                            >
                                <SelectTrigger
                                    className="h-11 w-full border-neutral-200 bg-white"
                                    aria-invalid={!!errors?.getError('organization.classification.area')}
                                >
                                    <SelectValue>
                                        {organization.classification.area
                                            ? t(`organization.classification.area.${organization.classification.area}`)
                                            : t(`${i18nKey}.classificationAreaPlaceholder`)}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {(
                                            Object.keys(
                                                OrganizationClassificationAreaSchema.enum
                                            ) as Array<OrganizationClassificationArea>
                                        ).map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {t(`organization.classification.area.${option}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <FieldError>{errors?.getError('organization.classification.area')}</FieldError>
                        </Field>
                    </div>

                    <Field
                        className={`w-full transition-opacity duration-200 ${
                            organization.classification.area === 'sports'
                                ? 'opacity-100'
                                : 'pointer-events-none opacity-0'
                        }`}
                    >
                        <FieldLabel required={organization.classification.area === 'sports'}>
                            {t(`${i18nKey}.sportsLabel`)}
                        </FieldLabel>
                        <Combobox<(typeof sportsOptions)[number], true>
                            multiple
                            items={sportsOptions}
                            value={selectedSportsOptions}
                            autoHighlight
                            isItemEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
                            itemToStringValue={(option) => option.value}
                            itemToStringLabel={(option) => option.label}
                            onValueChange={(options) => {
                                runInAction(() => {
                                    organization.classification.sports = options.map((option) => option.value)
                                })
                            }}
                        >
                            <ComboboxChips ref={sportsAnchor} className="w-full border-neutral-200 bg-white">
                                <ComboboxValue>
                                    {(options: typeof sportsOptions) => (
                                        <>
                                            {options.map((option) => (
                                                <ComboboxChip key={option.value}>{option.label}</ComboboxChip>
                                            ))}
                                            <ComboboxChipsInput
                                                required={organization.classification.area === 'sports'}
                                                aria-invalid={!!errors?.getError('organization.classification.sports')}
                                                placeholder={
                                                    options.length === 0 ? t(`${i18nKey}.sportsLabel`) : undefined
                                                }
                                            />
                                        </>
                                    )}
                                </ComboboxValue>
                                <ComboboxTrigger render={<NewButton type="button" variant="ghost" size="icon-xs" />} />
                            </ComboboxChips>
                            <ComboboxContent anchor={sportsAnchor}>
                                <ComboboxEmpty />
                                <ComboboxList>
                                    <ComboboxCollection>
                                        {(option: (typeof sportsOptions)[number]) => (
                                            <ComboboxItem key={option.value} value={option}>
                                                {option.label}
                                            </ComboboxItem>
                                        )}
                                    </ComboboxCollection>
                                </ComboboxList>
                            </ComboboxContent>
                        </Combobox>
                        <FieldError>{errors?.getError('organization.classification.sports')}</FieldError>
                    </Field>

                    <Field>
                        <FieldLabel>{t(`${i18nKey}.addressLabel`)}</FieldLabel>
                        <AddressForm
                            autoComplete={false}
                            required
                            autoCompleteOff
                            className="flex-1 [&_[data-slot=field-label]]:text-neutral-800 [&_[data-slot=input]]:border-neutral-200 [&_[data-slot=input]]:bg-white [&_[data-slot=input]]:text-neutral-950 [&_[data-slot=select-trigger]]:border-neutral-200 [&_[data-slot=select-trigger]]:bg-white [&_[data-slot=select-trigger]]:text-neutral-950"
                            showLabels={false}
                            name="organization.address"
                            details1="availableRecipient"
                            details2="notAvailable"
                            errors={errors ?? undefined}
                            restrictCountries={newOrganizationAvailableCountries}
                            preferredCountry={newOrganizationAvailableCountries[0]}
                            value={organization.address as Address}
                            onChange={(newAddress) => {
                                const country = newAddress.country as OrganizationCountryCode

                                if (!newOrganizationAvailableCountries.includes(country)) {
                                    return
                                }

                                runInAction(() => {
                                    organization.address = { ...newAddress, country }
                                })
                            }}
                        />
                        <FieldDescription className="text-neutral-600">{t(`${i18nKey}.addressHint`)}</FieldDescription>
                    </Field>
                </div>

                <div className="mt-6 flex justify-center">
                    <Button
                        disabled={!onNext}
                        icon={<ArrowRightIcon className="size-4" />}
                        mode="label"
                        onClick={onNext ?? undefined}
                    >
                        {t(`${i18nKey}.continue`)}
                    </Button>
                </div>
            </div>
        )
    }
)

export const createOrganization = (input: CreateOrganizationInput): Step =>
    observable({
        id: 'organization',
        Element,
        get isAvailable() {
            return true
        },
        get isValid() {
            return (
                !!input.organization.classification.type &&
                !!input.organization.classification.area &&
                (input.organization.classification.area !== 'sports' || input.organization.classification.sports.length > 0) &&
                !!input.organization.address.addressLine.trim() &&
                !!input.organization.address.zip.trim() &&
                !!input.organization.address.city.trim() &&
                !!input.organization.address.country
            )
        },
    })
